import React from 'react';
import { motion } from 'framer-motion';
import './A2ALandingPage.css';
import {
    Beat,
    Button,
    ContactDialog,
    FounderCard,
    GlassPanel,
    In,
    Kicker,
    LayerStack,
    LoopRing,
    ProofTile,
    Reveal,
    ScrollProgress,
    Section,
    SiteFooter,
    SiteNav,
    StatTile,
} from '../ds';
import { DUR, EASE_OUT, STAGGER, VARIANTS, container, useMotionOK } from '../ds/motion';
import {
    DoodleArm,
    DoodleChip,
    DoodleCoins,
    DoodleFleet,
    DoodleRover,
    DoodleStopwatch,
    DoodleTeach,
    HandArrow,
} from '../ds/components/Hand';
import { Rough } from '../ds/components/Rough';
import { useTilt } from '../ds/hooks/useTilt';
import type { ProofCount } from '../ds/components/ProofTile';

const VIEWPORT = { once: true, margin: '-10% 0px -10% 0px' } as const;

const PROOF: {
    value: string;
    unit?: string;
    label: string;
    to?: string;
    count?: ProofCount;
}[] = [
    {
        value: '82.5',
        unit: 'ms',
        label: 'ABC-VLA action chunks on a Snapdragon 8 Elite Gen 5 phone NPU, 2.5× faster than Qualcomm AI Hub FP16 and 1.8× faster than its INT8. Same checkpoint, ten flow steps.',
        to: '/blog/abcvla-snapdragon-phone',
    },
    {
        value: '310 → 442',
        unit: 'tok/s',
        count: { from: 310, to: 442, ms: 900, prefix: '310 → ' },
        label: 'Bonsai 1.7B decoding on M4 Max after the agents evolved faster ternary Metal kernels',
        to: '/blog/bonsai-ternary-metal-kernels',
    },
    {
        value: '1',
        unit: 'week',
        count: { from: 0, to: 1, ms: 420 },
        label: 'Time for ata to build an LLM evolution framework that beat AlphaEvolve on circle packing',
        to: '/blog/llm-evolution-framework-one-week',
    },
    {
        value: '#1',
        label: 'Best known score on the CloudCast broadcast benchmark, ahead of AdaEvolve, EvoX, and human experts',
        to: '/blog/cloudcast-best-known-score',
    },
];

const LAYERS = [
    {
        name: 'A2A autonomous engineer',
        badge: 'A2A',
        own: true,
        desc: 'Builds the task model or improves the one you already have. It engineers that intelligence into the full robot system, then uses failures from simulation and hardware to build the next version.',
    },
    {
        name: 'Coding agents and ML tools',
        badge: 'Tools A2A uses',
        desc: 'General coding agents can contribute code to a robotics project. A2A runs the engineering loop around the physical robot. It uses real failures to decide what must change across the stack, then validates the next version on hardware.',
    },
    {
        name: 'Robot model',
        badge: 'Open or proprietary',
        desc: 'Turns sensor input and task intent into robot actions. It can be a foundation model or a task-specific controller.',
    },
    {
        name: 'Robot hardware',
        badge: 'Your robot',
        desc: 'The physical system where every decision is tested. Its real-world results tell A2A what to improve next.',
    },
];

const TEACHING = [
    { lead: 'Tell it.', text: 'Describe the task in natural language.' },
    { lead: 'Show it.', text: 'Demonstrate the desired motion or result.' },
    { lead: 'Correct it.', text: 'Show what should happen when the robot fails.' },
    { lead: 'Guide it.', text: 'Take control when needed. A2A automatically turns your actions into new robot behavior.' },
];

const BELIEFS = [
    'The engineering loop is the durable advantage, more than any single model.',
    'A policy is only done when it works on the real robot, not in the sim or the lab.',
    'When a robot fails in the field, the fix should start that hour, not at the next release.',
    'The loop compounds. Every improvement upgrades every system.',
    'Make physical AI improve at software speed.',
    'Every robot deployment deserves its own autonomous engineer, not a queue for a human one.',
];

const AFFILIATIONS = [
    { src: 'https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg', alt: 'Apple' },
    { src: '/university-of-waterloo-96.webp', alt: 'University of Waterloo' },
    { src: '/university-of-maryland.svg', alt: 'University of Maryland' },
];

function AffiliationStrip({ className = '', motionOK }: { className?: string; motionOK: boolean }) {
    return (
        <motion.div
            className={`affil ${className}`.trim()}
            variants={container(STAGGER.tight, 0.12)}
            initial={motionOK ? 'hidden' : false}
            whileInView="shown"
            viewport={VIEWPORT}
        >
            {AFFILIATIONS.map((a) => (
                <motion.span className="affil__item ds-affil-lift" key={a.alt} variants={VARIANTS.fadeIn}>
                    <img src={a.src} alt={a.alt} />
                </motion.span>
            ))}
            <motion.span className="affil__item ds-affil-lift" variants={VARIANTS.fadeIn}>
                <img src="/nvidia-logo.svg" alt="NVIDIA" />
                <span className="affil__label">NVIDIA Inception member</span>
            </motion.span>
        </motion.div>
    );
}

function usePageBackground() {
    React.useEffect(() => {
        const prevHtml = document.documentElement.style.backgroundColor;
        const prevBody = document.body.style.backgroundColor;
        document.documentElement.style.backgroundColor = 'var(--surface-page)';
        document.body.style.backgroundColor = 'var(--surface-page)';
        return () => {
            document.documentElement.style.backgroundColor = prevHtml;
            document.body.style.backgroundColor = prevBody;
        };
    }, []);
}

/* A list whose items arrive one after the other. The items are the `li`
   themselves, so the rules they sit on stay attached to them. */
function StaggerList({
    className,
    stagger = STAGGER.base,
    delay = 0,
    children,
}: {
    className: string;
    stagger?: number;
    delay?: number;
    children: React.ReactNode;
}) {
    const ok = useMotionOK();
    return (
        <motion.ul
            className={className}
            variants={container(stagger, delay)}
            initial={ok ? 'hidden' : false}
            whileInView="shown"
            viewport={VIEWPORT}
        >
            {children}
        </motion.ul>
    );
}

export default function A2ALandingPage() {
    usePageBackground();
    const [contactOpen, setContactOpen] = React.useState(false);
    const heroTilt = useTilt<HTMLDivElement>({ max: 6, sheen: true });
    const answerTilt = useTilt<HTMLDivElement>({ max: 2.5, sheen: true });
    const motionOK = useMotionOK();

    React.useEffect(() => {
        document.title = 'Agents2Agents: The autonomous engineer for robotics';
    }, []);

    return (
        <div className="ds-page a2a-field a2a-intro">
            <ScrollProgress />
            <SiteNav />

            {/* ── Hero ── */}
            <section className="hero">
                <div className="ds-container ds-container--xl hero__grid">
                    <div className="hero__copy">
                        <h1 className="hero__title">
                            <Beat as="span" variant="clipLeft" delay={0.14} className="hero__line">
                                Take a robot
                            </Beat>{' '}
                            <Beat as="span" variant="clipLeft" delay={0.21} className="hero__line">
                                task from idea
                            </Beat>{' '}
                            <Beat as="span" variant="clipLeft" delay={0.28} className="hero__line">
                                to{' '}
                                <Rough
                                    className="ds-rough--circle"
                                    type="circle"
                                    strokeWidth={2.25}
                                    padding={[10, 22]}
                                    iterations={1}
                                    animationDuration={900}
                                    delay={900}
                                >
                                    deployment.
                                </Rough>
                            </Beat>
                        </h1>
                        <Beat as="p" delay={0.52} className="hero__sub">
                            A2A is the autonomous engineer for robotics. It can start from a written instruction or
                            demonstration, or take over an existing system. Its agents carry the task from simulation
                            to real hardware, then continue improving the system after deployment.
                        </Beat>
                        <Beat delay={0.62} className="hero__cta">
                            <Button to="/blog" size="lg" pill>
                                See what A2A agents have built
                            </Button>
                        </Beat>
                        <Beat variant="fadeIn" delay={0.78}>
                            <AffiliationStrip className="affil--hero" motionOK={motionOK} />
                        </Beat>
                        <Beat as="p" variant="fadeIn" delay={0.9} className="hero__status">
                            Now building for robotic manipulation and on-device deployment.
                        </Beat>
                    </div>

                    <div className="hero__stage">
                        <Beat variant="rise" delay={0.3} className="hero__slot">
                            <GlassPanel
                                ref={heroTilt.ref}
                                onPointerMove={heroTilt.onPointerMove}
                                onPointerLeave={heroTilt.onPointerLeave}
                                radius="2xl"
                                className="hero__panel ds-glass--3d ds-glass--sheen"
                            >
                                <div className="hero__marklift">
                                    <img className="hero__mark" src="/brand/mark-glyph-564.webp" alt="Agents2Agents" />
                                </div>
                            </GlassPanel>
                        </Beat>
                        <DoodleArm className="hero__doodle" draw />
                    </div>
                </div>
            </section>

            {/* ── Problem / answer ── */}
            <Section className="gap">
                <div className="gap__grid">
                    <div className="gap__left">
                        <In as="h2" className="h2">
                            Robotics demos work, yet real-world systems remain{' '}
                            <Rough type="underline" strokeWidth={2.5} padding={3} delay={420}>
                                unreliable
                            </Rough>
                            .
                        </In>
                    </div>
                    <Reveal variant="hold" className="gap__right ds-draw-rules">
                        <StaggerList className="rule-list rule-list--lead">
                            <motion.li variants={VARIANTS.fadeUp}>
                                <b>Brittle outside controlled conditions.</b> A system can work in one setup and fail
                                when the object, scene, or lighting changes. Many never make it beyond the demo.
                            </motion.li>
                            <motion.li variants={VARIANTS.fadeUp}>
                                <b>Slow engineering cycles.</b> A single failure can lead to weeks of reviewing logs,
                                collecting data, retraining models, and validating the next version on hardware.
                            </motion.li>
                            <motion.li variants={VARIANTS.fadeUp}>
                                <b>Models that cannot run on the robot.</b> A model can perform well in the lab but run
                                too slowly or consume too many resources on the robot's hardware.
                            </motion.li>
                        </StaggerList>
                        <In variant="rise" delay={0.2}>
                            <div
                                ref={answerTilt.ref}
                                onPointerMove={answerTilt.onPointerMove}
                                onPointerLeave={answerTilt.onPointerLeave}
                                className="gap__answer ds-glass ds-glass--strong ds-glass--3d ds-glass--sheen"
                            >
                                Every robot failure creates another engineering task. Today, engineers handle that
                                task across disconnected tools and validate the result on hardware. A2A is built to run
                                this cycle itself, turning each failure into an improvement on the robot.
                            </div>
                        </In>
                        <In as="p" delay={0.12} className="key-line">
                            Models control the robot. A2A builds those models and engineers the system that makes them
                            work in the real world.
                        </In>
                    </Reveal>
                </div>
            </Section>

            {/* ── The loop ── */}
            <Section className="loop">
                <div className="loop__head">
                    <Kicker tilt="-2deg">the a2a loop</Kicker>
                    <In as="h2" className="h2">
                        A2A runs the full robotics engineering cycle.
                    </In>
                    <In as="p" delay={0.08} className="lede">
                        Each task moves through simulation, training, hardware optimization, and testing on the robot.
                        A2A determines why the robot failed, then builds the next version to fix the cause.
                    </In>
                </div>
                <LoopRing />
                <In delay={0.12} className="loop__notes ds-glass ds-glass--strong ds-glass--r-2xl">
                    <article className="loop-note">
                        <DoodleRover className="loop-note__icon" draw />
                        <h3 className="h4">Learns from the robot</h3>
                        <p>
                            Deployed systems show A2A what works and what fails. A2A uses that evidence to build and
                            redeploy the next version.
                        </p>
                    </article>
                    <article className="loop-note">
                        <DoodleChip className="loop-note__icon" draw />
                        <h3 className="h4">Learns from new research</h3>
                        <p>
                            A2A tests ideas from new papers and code against its current system. Proven improvements
                            become part of the stack.
                        </p>
                    </article>
                </In>
            </Section>

            {/* ── Robot teaching ── */}
            <Section className="teach">
                <div className="teach__grid">
                    <div className="teach__left">
                        <Kicker tilt="-1.5deg">teach it, don't program it</Kicker>
                        <In as="h2" className="h2">
                            Tell it. Show it. Correct it.
                        </In>
                        <In as="p" delay={0.08} className="teach__body">
                            Teaching a robot a new task should not require a robotics engineer, an ML engineer,
                            a data engineer, a 3D engineer, and a kernel engineer at every step. A2A turns your
                            instructions and demonstrations into a system that performs the task on real hardware.
                        </In>
                        <In as="p" delay={0.16} className="teach__closing">
                            You teach the task. A2A does the engineering.
                        </In>
                        <DoodleTeach className="teach__doodle" draw />
                    </div>
                    <Reveal variant="hold" className="ds-draw-rules">
                        <StaggerList className="steps">
                            {TEACHING.map((step, i) => (
                                <motion.li className="steps__item" key={step.lead} variants={VARIANTS.fadeUp}>
                                    <span className="steps__idx">{i + 1}.</span>
                                    <p className="steps__text">
                                        <b>{step.lead}</b> {step.text}
                                    </p>
                                </motion.li>
                            ))}
                        </StaggerList>
                    </Reveal>
                </div>
            </Section>

            {/* ── Proof band ── */}
            <section className="proof" data-theme="dark">
                <div className="ds-container">
                    <div className="proof__head">
                        <Kicker tilt="-2.2deg">early evidence</Kicker>
                        <In as="h2" className="h2">
                            The agents are already producing measurable gains.
                        </In>
                        <In as="p" delay={0.08} className="lede">
                            They have accelerated robot control on embedded hardware and built methods that beat
                            established research baselines. We are now connecting that work across the full robotics
                            engineering loop.
                        </In>
                    </div>
                    <div className="proof__gridwrap">
                        <DoodleChip className="proof__doodle" draw />
                        <motion.div
                            className="proof__grid"
                            variants={container(STAGGER.loose)}
                            initial={motionOK ? 'hidden' : false}
                            whileInView="shown"
                            viewport={VIEWPORT}
                        >
                            {PROOF.map((p) => (
                                <motion.div
                                    key={p.value + p.label}
                                    className="proof__slot"
                                    variants={{
                                        hidden: { opacity: 0, y: 28, rotateX: 14 },
                                        shown: {
                                            opacity: 1,
                                            y: 0,
                                            rotateX: 0,
                                            transition: { duration: DUR.glass, ease: EASE_OUT },
                                        },
                                    }}
                                >
                                    <ProofTile {...p} />
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                    <In delay={0.1} className="proof__foot">
                        <Button to="/blog" variant="ghost" size="md" pill arrow>
                            All posts
                        </Button>
                    </In>
                </div>
            </section>

            {/* ── Layers ── */}
            <Section className="layers">
                <div className="layers__head">
                    <div className="head-row">
                        <In as="h2" className="h2">
                            A2A builds and improves the full engineering stack behind the robot.
                        </In>
                        <Kicker tilt="-1.6deg">where a2a sits</Kicker>
                    </div>
                    <In as="p" delay={0.08} className="layers__intro">
                        It can build a model for the task or start with one you already have. Its agents use today's
                        coding tools and simulators to carry that model onto real hardware, then improve it from
                        real-world results.
                    </In>
                </div>
                <LayerStack layers={LAYERS} />
                <In as="p" delay={0.12} className="key-line layers__key">
                    The tools keep getting better. Turning them into a robot that works is an engineering job.
                    A2A does that job.
                </In>
            </Section>

            {/* ── Robotics first ── */}
            <Section className="robotics">
                <div className="robotics__grid">
                    <div className="robotics__intro">
                        <In as="h2" className="h2">
                            Robotics first.
                        </In>
                        <In as="p" delay={0.08} className="body-lg robotics__lede">
                            Manipulation and edge deployment first. The gap between a demo and a working
                            deployment is widest there, so that is where the loop pays off soonest.
                        </In>
                        <DoodleRover className="robotics__doodle" draw />
                    </div>
                    <div className="robotics__cases">
                        <Reveal variant="hold" className="ds-draw-rules">
                            <StaggerList className="rule-list rule-list--lead">
                                <motion.li variants={VARIANTS.fadeUp}>
                                    <b>Robotic manipulation.</b> Teach arms to pick, present, hand off, assemble and
                                    inspect across changing scenes.
                                </motion.li>
                                <motion.li variants={VARIANTS.fadeUp}>
                                    <b>Deployment acceleration.</b> Adapt and optimize policies for the edge hardware
                                    already in the product or factory.
                                </motion.li>
                                <motion.li variants={VARIANTS.fadeUp}>
                                    <b>Industrial inspection and handling.</b> Repeatable behaviors for inspection, QA
                                    and material handling.
                                </motion.li>
                                <motion.li variants={VARIANTS.fadeUp}>
                                    <b>Robotics research.</b> Experiment setup, policy comparison, ablations, failure
                                    analysis, reproducible runs.
                                </motion.li>
                            </StaggerList>
                        </Reveal>
                        <In as="p" delay={0.1} className="mono-line">
                            For robotics startups, industrial teams, research labs, robot OEMs and chip-platform
                            partners
                        </In>
                    </div>
                </div>
            </Section>

            {/* ── What changes ── */}
            <Section className="changes">
                <Kicker tilt="-2deg">what changes</Kicker>
                <In as="h2" className="h2 changes__title">
                    What changes when the loop runs itself.
                </In>
                <motion.div
                    className="changes__grid"
                    variants={container(STAGGER.base)}
                    initial={motionOK ? 'hidden' : false}
                    whileInView="shown"
                    viewport={VIEWPORT}
                >
                    <motion.div variants={VARIANTS.rise}>
                        <StatTile
                            doodle={<DoodleStopwatch draw />}
                            label="Cycle time"
                            value="Hours"
                            sub="Not months. No human team runs this cycle at this speed. That is the point."
                        />
                    </motion.div>
                    <motion.div variants={VARIANTS.rise}>
                        <StatTile
                            doodle={<DoodleFleet draw />}
                            label="Reach"
                            value="Every system"
                            sub="A better architecture does not improve one system. It upgrades every system."
                        />
                    </motion.div>
                    <motion.div variants={VARIANTS.rise}>
                        <StatTile
                            doodle={<DoodleCoins draw />}
                            label="Cost"
                            value="A fraction"
                            sub="A failure mode at one site becomes a known pattern everywhere."
                        />
                    </motion.div>
                </motion.div>
            </Section>

            {/* ── What we believe ── */}
            <Section className="beliefs" tight>
                <Kicker tilt="-2.4deg">what we believe</Kicker>
                <Reveal variant="hold" className="ds-draw-rules">
                    <motion.div
                        className="beliefs__grid"
                        variants={container(STAGGER.tight)}
                        initial={motionOK ? 'hidden' : false}
                        whileInView="shown"
                        viewport={VIEWPORT}
                    >
                        {BELIEFS.map((b, i) => (
                            <motion.div className="beliefs__cell" key={b} variants={VARIANTS.fadeUp}>
                                <span className="beliefs__idx">{String(i + 1).padStart(2, '0')}</span>
                                <p className="beliefs__text">{b}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </Reveal>
                <In as="p" className="beliefs__closing">
                    The expert sets the objective. The system does the engineering.
                </In>
            </Section>

            {/* ── Founders ── */}
            <Section className="founders">
                <div className="founders__head">
                    <div className="head-row">
                        <In as="h2" className="h2">
                            Built by ex-Apple ML engineers.
                        </In>
                        <Kicker tilt="-1.8deg">who we are</Kicker>
                    </div>
                    <In as="p" delay={0.08} className="lede">
                        Built and deployed large-scale ML and physical-AI systems, including computer-vision and
                        manufacturing work at Apple.
                    </In>
                    <span className="hand-note founders__note" style={{ '--tilt': '1.4deg' } as React.CSSProperties}>
                        <HandArrow direction="down-right" width={40} draw />
                        <In as="span" variant="clipLeft" delay={0.6}>
                            the two of us
                        </In>
                    </span>
                </div>
                <div className="founders__grid">
                    <In variant="drop" as="div" className="founders__slot">
                        <FounderCard
                            name="Tho Ho"
                            role="Co-founder & CEO"
                            photo="/tho-headshot.webp"
                            linkedin="https://www.linkedin.com/in/tho-ho-51568213/"
                        />
                    </In>
                    <In variant="drop" delay={0.12} as="div" className="founders__slot">
                        <FounderCard
                            name="Nima Sadri"
                            role="Co-founder & Chief of Agents"
                            photo="/nima-headshot-820.webp"
                            linkedin="https://www.linkedin.com/in/nimasadri/"
                        />
                    </In>
                    <In delay={0.16} className="founders__why">
                        <h3 className="h4">Why us</h3>
                        <Reveal variant="hold" className="ds-draw-rules">
                            <StaggerList className="rule-list rule-list--sm">
                                <motion.li variants={VARIANTS.fadeUp}>
                                    Developed and deployed manufacturing and inspection robotics and the models behind
                                    them, including Apple's iPhone, iPad and Mac QA systems.
                                </motion.li>
                                <motion.li variants={VARIANTS.fadeUp}>
                                    Built wheeled robots that navigate the real world, and fifteen years of production
                                    sensor data behind them.
                                </motion.li>
                                <motion.li variants={VARIANTS.fadeUp}>
                                    Real-world failures turned into production models in days, not months.
                                </motion.li>
                            </StaggerList>
                        </Reveal>
                    </In>
                </div>
                <Reveal variant="hold" className="ds-draw-rules">
                    <AffiliationStrip motionOK={motionOK} />
                </Reveal>
            </Section>

            {/* ── Closing ── */}
            <section className="closing">
                <div className="ds-container ds-container--md">
                    <In as="h2" className="closing__title">
                        Give us a robot and{' '}
                        <Rough
                            className="ds-rough--circle"
                            type="circle"
                            strokeWidth={2.25}
                            padding={[10, 22]}
                            iterations={1}
                            animationDuration={900}
                            delay={520}
                        >
                            a task.
                        </Rough>
                    </In>
                    <In as="p" delay={0.08} className="closing__sub">
                        We are working with robotics teams and hardware partners on manipulation, task learning and
                        edge deployment. If you have a policy that is too brittle, too slow, or too expensive to
                        adapt, we want to see it.
                    </In>
                    <In delay={0.16} className="closing__cta">
                        <Button size="lg" pill onClick={() => setContactOpen(true)}>
                            Contact us
                        </Button>
                        <Button to="/blog" variant="ghost" size="lg" pill>
                            Read the blog
                        </Button>
                    </In>
                    <p className="hand-note closing__note" style={{ '--tilt': '-1.2deg' } as React.CSSProperties}>
                        <In as="span" variant="clipLeft" delay={0.3}>
                            or email us at <a href="mailto:founders@agents2agents.ai">founders@agents2agents.ai</a>
                        </In>
                    </p>
                </div>
            </section>

            <In variant="fadeIn">
                <SiteFooter />
            </In>
            <ContactDialog open={contactOpen} onClose={() => setContactOpen(false)} />
        </div>
    );
}
